import React, { useState } from 'react';

export default function Tutorial({ onNavigate }) {
  const [expandedStep, setExpandedStep] = useState(0);

  // Cada tarjeta describe lo que la pantalla hace HOY. Las descripciones
  // anteriores prometían cosas que no existían —descarga a Excel, sincronizar
  // sin conexión, una pantalla de Documentos que no está en el menú— y esto lo
  // ve quien acaba de pagar por el programa.
  const steps = [
    {
      title: '🏠 Inicio',
      description: 'El resumen de tu negocio, para la primera mirada del día',
      details: [
        '• Cuánto tienes prestado ahora mismo',
        '• Cuánto llevas ganado en intereses',
        '• La ganancia proyectada del mes',
        '• Tu mejor cliente y el que necesita atención'
      ],
      icon: '🏠', action: 'dashboard', color: '#3b82f6'
    },
    {
      title: '👥 Clientes',
      description: 'Tu base de datos de personas',
      details: [
        '• Caja de búsqueda: por nombre, cédula o teléfono. Sin tildes también',
        '• Cédula, nombre, teléfono y dirección',
        '• El ➕ de la fila le presta otra vez, sin salir de la lista',
        '   Cada préstamo lleva su propia cuenta de interés',
        '• El lápiz corrige datos del cliente y de sus préstamos',
        '• Si tiene varios préstamos, pestañas para elegir cuál corregir',
        '• El monto de la fila suma todos sus préstamos abiertos',
        '• Antes de borrar te dice cuánto debe todavía, con cifras'
      ],
      icon: '👥', action: 'clients', color: '#10b981'
    },
    {
      title: '➕ Prestar',
      description: 'Prestar, con las cuentas a la vista antes de guardar',
      details: [
        '• Monto, interés y cada cuánto se cobra',
        '• Vista previa: cuota, número de cobros y tu ganancia',
        '• Casilla de fiador: nombre, cédula, teléfono, dirección y correo',
        '• Casilla de préstamo abierto, si no hay fecha de fin pactada',
        '• Con fecha de vencimiento sale en Vencimientos; sin ella, no'
      ],
      icon: '➕', action: 'new-loan', color: '#f59e0b'
    },
    {
      title: '💳 Pagos',
      description: 'Lo único que hay que hacer a diario',
      details: [
        '• REPARTIR SOLO: anotas lo que te entregó y él cubre primero el interés',
        '   y con lo que sobra baja el capital. Te enseña el reparto antes de guardar',
        '• Con VARIOS PRÉSTAMOS: eliges «Repartir entre sus N préstamos» y anotas',
        '   lo entregado una sola vez. Cubre el interés de todos, del más antiguo',
        '   al más nuevo, y lo que sobre baja el capital del más antiguo',
        '• Si prefieres, marcas tú mismo INTERÉS o CAPITAL',
        '• El interés no baja la deuda; el capital sí, y baja también el interés',
        '• Te muestra el interés acumulado, no solo el de un periodo',
        '• Puedes abonar de menos: lo que falte se arrastra',
        '• El lápiz corrige monto, fecha y hasta si fue interés o capital',
        '• Sin señal puedes seguir cobrando: se guarda y se sube solo'
      ],
      icon: '💳', action: 'payments', color: '#10b981'
    },
    {
      title: '🧮 Deuda',
      description: 'La respuesta a «¿cuánto debo?»',
      details: [
        '• Buscas por nombre, cédula o teléfono y sale todo de una',
        '• Capital pendiente, interés acumulado y el total',
        '• Con varios préstamos: el total arriba y cada préstamo por separado,',
        '   con su historial. Los cerrados salen como historial, sin sumar',
        '• El historial completo, marcando qué fue interés y qué capital',
        '• Botón para CERRAR el préstamo cuando termina',
        '• Al cerrar puedes aceptar menos: lo condonado queda registrado'
      ],
      icon: '🧮', action: 'deuda', color: '#8b5cf6'
    },
    {
      title: '⚠️ Morosos',
      description: 'A quién llamar hoy, en tres grupos',
      details: [
        '• Pagan de menos: al día en fechas, pero deben interés acumulado',
        '• Dejaron de pagar: venían abonando y se cayeron',
        '• Nunca han pagado: ni un abono desde que empezó',
        '• El plazo sale del propio préstamo, no de un número fijo',
        '• Teléfono, dirección, WhatsApp y el fiador en cada fila'
      ],
      icon: '⚠️', action: 'delinquents', color: '#ef4444'
    },
    {
      title: '📋 Vencimientos',
      description: 'Qué préstamos se acaban y cuándo',
      details: [
        '• Ordenados por fecha de vencimiento',
        '• 🔴 vencido · 🟡 vence esta semana · 🟢 hay tiempo',
        '• Los préstamos abiertos no salen: no tienen fecha que vigilar'
      ],
      icon: '📋', action: 'overdue-loans', color: '#0ea5e9'
    },
    {
      title: '💰 Ganancias',
      description: 'Lo que te ha dejado cada cliente',
      details: [
        '• Tus ganancias mes a mes en una gráfica',
        '• Top 10 de clientes por lo que te han dejado',
        '• Útil para saber a quién conviene volver a prestarle'
      ],
      icon: '💰', action: 'gains', color: '#eab308'
    },
    {
      title: '📈 Cobranza',
      description: 'Si estás prestando bien o solo prestando mucho',
      details: [
        '• Tu tasa de cobro: de lo que debías recoger, cuánto recogiste',
        '• Cobros por mes',
        '• Los clientes que mejor pagan'
      ],
      icon: '📈', action: 'cobranza', color: '#06b6d4'
    },
    {
      title: '💸 Gastos',
      description: 'Lo que sale, con aviso si algo se dispara',
      details: [
        '• Doce categorías: arriendo, transporte, servicios...',
        '• Cuánto te desvías de tu promedio y en cuánto cerrarías',
        '• Lo cobrado contra lo gastado: en rojo si gastaste más',
        '• Te avisa si una categoría se sale de lo normal',
        '• Con menos de tres meses registrados no opina'
      ],
      icon: '💸', action: 'expenses', color: '#ec4899'
    },
    {
      title: '👁️ Reputación',
      description: 'Quién te paga bien, según su historial',
      details: [
        '• Puntaje de 0 a 100, recalculado con cada pago',
        '• Puntualidad (40) + volumen (30) + consistencia (30)',
        '• Un cliente nuevo empieza bajo y sube pagando',
        '• Insignias que se ganan solas'
      ],
      icon: '👁️', action: 'public-profiles', color: '#6366f1'
    },
    {
      title: '⚙️ Configuración',
      description: 'Se ajusta una vez, al empezar',
      details: [
        '• Tu interés y tu frecuencia de cobro por defecto',
        '• Tu correo de Gmail para los recordatorios automáticos',
        '  (necesita una contraseña de aplicación, no la normal)',
        '• Repartir clientes entre socios, si trabajas así'
      ],
      icon: '⚙️', action: 'settings', color: '#f97316'
    }
  ];

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '40px',
        padding: '30px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        color: 'white'
      }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '32px' }}>🚀 Bienvenido a FlexFlow Pro</h1>
        <p style={{ margin: 0, fontSize: '16px', opacity: 0.9 }}>
Toca cualquier tarjeta para ver qué hace esa pantalla y saltar a ella
        </p>
      </div>

      {/* Quick Start */}
      <div style={{
        marginBottom: '40px',
        padding: '20px',
        backgroundColor: '#f0fdf4',
        border: '2px solid #10b981',
        borderRadius: '8px'
      }}>
        <h3 style={{ margin: '0 0 10px 0', color: '#10b981' }}>⚡ INICIO RÁPIDO (4 pasos)</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px'
        }}>
          <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>1️⃣</div>
            <strong>Agrega clientes</strong>
            <p style={{ margin: '5px 0', fontSize: '13px', color: '#6b7280' }}>En 👥 Clientes</p>
          </div>
          <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>2️⃣</div>
            <strong>Crea préstamos</strong>
            <p style={{ margin: '5px 0', fontSize: '13px', color: '#6b7280' }}>En ➕ Nuevo Préstamo</p>
          </div>
          <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>3️⃣</div>
            <strong>Registra pagos</strong>
            <p style={{ margin: '5px 0', fontSize: '13px', color: '#6b7280' }}>En 💳 Pagos</p>
          </div>
          <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>4️⃣</div>
            <strong>Cierra los que terminan</strong>
            <p style={{ margin: '5px 0', fontSize: '13px', color: '#6b7280' }}>En 🧮 Deuda</p>
          </div>
        </div>
      </div>

      {/* Módulos */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{ marginBottom: '20px', textAlign: 'center' }}>📚 Todos los Módulos</h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '15px'
        }}>
          {steps.map((step, idx) => (
            <div
              key={idx}
              onClick={() => setExpandedStep(expandedStep === idx ? -1 : idx)}
              style={{
                backgroundColor: 'white',
                border: `2px solid ${step.color}`,
                borderRadius: '8px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.3s',
                transform: expandedStep === idx ? 'scale(1.05)' : 'scale(1)',
                boxShadow: expandedStep === idx ? `0 8px 16px ${step.color}33` : '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '32px' }}>{step.icon}</span>
                <h3 style={{ margin: 0, color: step.color }}>{step.title}</h3>
              </div>
              <p style={{ margin: '10px 0', color: '#6b7280', fontSize: '14px' }}>
                {step.description}
              </p>

              {expandedStep === idx && (
                <div style={{
                  marginTop: '15px',
                  paddingTop: '15px',
                  borderTop: `1px solid ${step.color}33`
                }}>
                  {step.details.map((detail, i) => (
                    <p key={i} style={{ margin: '8px 0', fontSize: '13px', color: '#374151' }}>
                      {detail}
                    </p>
                  ))}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate(step.action);
                    }}
                    style={{
                      marginTop: '15px',
                      width: '100%',
                      padding: '10px',
                      backgroundColor: step.color,
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                  >
                    ✨ Ir a {step.title}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div style={{
        backgroundColor: '#fef3c7',
        border: '2px solid #f59e0b',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '30px'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#d97706' }}>💡 Tips importantes</h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#92400e' }}>
          <li>Anota los pagos el mismo día: de ahí sale todo lo demás</li>
          <li><strong>Revisa el reparto antes de guardar.</strong> Sale en pantalla mientras escribes el monto, y es lo que decide si la deuda baja o no</li>
          <li>Cuando un préstamo termina, ciérralo desde 🧮 Deuda para que salga de las listas</li>
          <li>Sin señal puedes seguir cobrando: los abonos se suben solos al volver el internet</li>
          <li>Si te equivocas en un monto, se puede corregir: pagos, gastos y préstamos</li>
          <li>Cada noche a las 2 se guarda una copia, y se conservan 30 días</li>
          <li>Puedes instalarla en el celular: en Android desde el menú de Chrome, en iPhone desde Safari → Compartir → Añadir a pantalla de inicio</li>
          <li>Tus datos son solo tuyos: ninguna otra empresa los ve</li>
        </ul>
      </div>

      {/* Support */}
      <div style={{
        backgroundColor: '#f0f9ff',
        border: '2px solid #3b82f6',
        borderRadius: '8px',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h3 style={{ margin: '0 0 10px 0', color: '#3b82f6' }}>❓ ¿Preguntas o soporte?</h3>
        <p style={{ margin: '0', color: '#6b7280' }}>
          Tienes el manual completo en <a href="/manual" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>miflexflow.com/manual</a>, con
          las preguntas más frecuentes.
        </p>
      </div>
    </div>
  );
}
